import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { Emoji } from "emoji-picker-react";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { PlannerContext } from "../../../../../context/planner/PlannerContext";

/**
 * Format duration in minutes to a readable string
 */
const formatDuration = (minutes) => {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  return `${hours}h`;
};

/**
 * Reusable activity selector dropdown for block editing/creation
 *
 * @param {Object} props
 * @param {Array} props.activities - Array of activity objects with id, name, color, and optional duration
 * @param {string} props.value - Currently selected activity ID
 * @param {function} props.onChange - Callback when activity selection changes
 * @param {string} [props.label="Activity"] - Label for the select field
 * @param {boolean} [props.allowNone=true] - Whether to show "None" option
 * @param {number} [props.blockDurationMinutes] - Duration of the block in minutes, used to filter activities
 */
const ActivitySelector = ({
  activities,
  value,
  onChange,
  label = "Activity",
  allowNone = true,
  blockDurationMinutes,
}) => {
  const navigate = useNavigate();
  const { selectedBlockId, selectedTool, currentDate } =
    useContext(PlannerContext);

  // Filter activities based on block duration if provided
  const filteredActivities = blockDurationMinutes
    ? activities.filter(
        (activity) =>
          !activity.duration || activity.duration <= blockDurationMinutes,
      )
    : activities;

  const hasNoFittingActivities =
    blockDurationMinutes && filteredActivities.length === 0;

  // Handle navigation to activities page
  const handleNavigateToActivities = () => {
    navigate("/activities", {
      state: {
        returnTo: "/planner",
        selectedBlockId,
        selectedTool,
        currentDate,
      },
    });
  };

  // If no activities at all, show full-width "Create activities" button
  if (activities.length === 0) {
    return (
      <Button
        variant="outlined"
        size="large"
        fullWidth
        onClick={handleNavigateToActivities}
        sx={{
          py: 1.5,
          borderStyle: "dashed",
          borderColor: "primary.main",
          color: "primary.main",
          "&:hover": {
            borderStyle: "solid",
            backgroundColor: "primary.main",
            color: "white",
          },
        }}
      >
        Create activities
      </Button>
    );
  }

  if (hasNoFittingActivities) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          borderRadius: 1,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No activities fit within this time block. Try extending the block
          duration or creating activities with shorter time requirements.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <FormControl variant="outlined" fullWidth size="small">
        <InputLabel id="activity-select-label">{label}</InputLabel>
        <Select
          labelId="activity-select-label"
          variant="outlined"
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
              },
            },
          }}
        >
          {allowNone && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
          {filteredActivities.map((activity) => (
            <MenuItem key={activity.id} value={activity.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    backgroundColor: activity.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {activity.emoji && (
                    <Emoji unified={activity.emoji} size={14} />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <span>{activity.name}</span>
                  {activity.duration && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "text.secondary", ml: "auto" }}
                    >
                      {formatDuration(activity.duration)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        onClick={handleNavigateToActivities}
        size="small"
        sx={{
          mt: 0.5,
          color: "primary.main",
          "&:hover": {
            backgroundColor: "rgba(25, 123, 104, 0.1)",
          },
        }}
        title="New activity"
      >
        <Add />
      </IconButton>
    </Box>
  );
};

export default ActivitySelector;
